import { ObstacleType } from '../obstacles/ObstacleType';

/**
 * Difficulty scales with run distance – not only via speed, but via density,
 * combinations and the obstacle types that may appear.
 */
export interface DifficultyTier {
  readonly id: string;
  /** Name of the tier (debug overlay; the player sees no level since 2026-10-03). */
  readonly title: string;
  /** Tier is active from this run distance (meters) on. */
  readonly fromDistance: number;
  /** Which row layout of a segment is used: sparse = one decision line, dense = two. */
  readonly rowLayout: 'sparse' | 'dense';
  /** Probability that an obstacle row in a segment is actually filled. */
  readonly rowFillChance: number;
  readonly maxObstaclesPerRow: number;
  /**
   * At least this many obstacles per filled row (user 2026-10-05: "die Anzahl der Hindernisse und die Möglichkeit, durchzukommen"):
   * 2 = every row closes two lanes (or makes them jumps / slides). Relaxed to 1 in the later layout attempts when no
   * fair row is found – a row is never left empty for it.
   */
  readonly minObstaclesPerRow: number;
  /** Obstacle gameplay types that may be spawned in this tier, with weights. */
  readonly obstacleWeights: Readonly<Partial<Record<ObstacleType, number>>>;
  /**
   * Multiplies the chances of bottle lines and container-roof lines (fewer bottles = fewer extra lives, user
   * 2026-10-03: "wirklich weniger Flaschen … dass man sich so oft aufladen kann").
   */
  readonly bottleShare: number;
  /** Multiplies the chance of a bonus pickup on a line (helmet = extra life, toolbox). */
  readonly bonusShare: number;
  /** Multiplies the pick weight of modules with moving hazards (cranes, vehicles, excavator) – more of them later. */
  readonly hazardWeightScale: number;
  /**
   * Multiplies the pick weight of the plain straights (SEG_STRAIGHT_20M/40M: rows only) – fewer of them later, the
   * special modules (pits, scaffolds, containers, cranes, vehicles …) take their place: more different obstacles in
   * combination (user 2026-10-05: "die Kombinatorik verschiedener Hindernisse").
   */
  readonly plainWeightScale: number;
  /**
   * Multiplies the pick weight of the special modules without moving hazards – pits, scaffolds, pipe bridges,
   * containers, ramps, tunnels, earthworks: different obstacles in combination, not only more cranes and vehicles.
   */
  readonly specialWeightScale: number;
}

export interface DifficultyConfig {
  readonly speed: {
    readonly start: number;
    readonly max: number;
    /** Distance at which max speed is reached. */
    readonly rampDistance: number;
    /** Exponent of the ramp curve: <1 accelerates early, >1 late. */
    readonly rampExponent: number;
    /**
     * Faster start (user 2026-10-05: "ab 0 bis 1000 m … ruhig 20 % schneller laufen"): × `factor` up to `until` m, then
     * back to the plain ramp by `fadeOutAt` m (during the ructec-Bulli ride – at 1000 m the boosted speed is about the
     * plain speed at 2000 m, so nothing jumps).
     */
    readonly earlyBoost: { readonly factor: number; readonly until: number; readonly fadeOutAt: number };
  };
  /** Assumed human reaction time used by the sequence validator (seconds). */
  readonly reactionTime: number;
  readonly tiers: readonly DifficultyTier[];
}

/**
 * The top speed is 16 m/s from 4000 m on (user 2026-10-03: "es muss nicht unbedingt zum Schluss so mega schnell werden,
 * sondern die Hindernisse schwieriger" – was 18 m/s; every layout is laid out and proven up to 18 m/s, so 16 is inside
 * the tested range – not 15: the scaffold crossbars' double jump with a head bump misses exactly there, jumpHonesty). The run gets harder in steps at 2000, 4000, 6000 and 8000 m (user 2026-10-03: "ab 2000 anfangen,
 * ab 4000, ab 6000 – so Schritte"): fuller and harder obstacle rows, more moving hazards and fewer bottles and bonus
 * pickups – an endless run can no longer be kept alive by collecting. Since 2026-10-04 the steps come much earlier (user:
 */
export const DIFFICULTY_CONFIG: DifficultyConfig = {
  speed: {
    start: 8,
    max: 16,
    rampDistance: 4000,
    rampExponent: 0.8,
    earlyBoost: { factor: 1.2, until: 1000, fadeOutAt: 2000 },
  },
  reactionTime: 0.3,
  // user 2026-10-05: "es muss wirklich äußerst schwer sein, bis 4000 m zu kommen … nicht auf Geschwindigkeit bezogen, sondern
  // die Anzahl der Hindernisse und die Möglichkeit, durchzukommen, und die Kombinatorik verschiedener Hindernisse": the
  // steps come much earlier, rows block more lanes from 400 m on, the special modules replace the plain straights
  // (plainWeightScale) and the moving hazards come far more often (hazardWeightScale). Every row still passes the
  // sequence validator – hard, never impossible.
  tiers: [
    {
      id: 'T0_SINGLE_MECHANICS',
      title: 'Aufwärmen',
      rowLayout: 'sparse',
      fromDistance: 0,
      rowFillChance: 1,
      maxObstaclesPerRow: 2,
      minObstaclesPerRow: 1,
      obstacleWeights: { [ObstacleType.Jump]: 3, [ObstacleType.Slide]: 2, [ObstacleType.LaneChange]: 2 },
      bottleShare: 0.56, // was 0.7 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 1,
      hazardWeightScale: 1.3,
      plainWeightScale: 0.9,
      specialWeightScale: 1.2,
    },
    {
      id: 'T1_COMBINATIONS',
      title: 'Kombinationen',
      rowLayout: 'dense',
      fromDistance: 200,
      rowFillChance: 1,
      maxObstaclesPerRow: 2,
      minObstaclesPerRow: 1,
      obstacleWeights: {
        [ObstacleType.Jump]: 3,
        [ObstacleType.Slide]: 3,
        [ObstacleType.LaneChange]: 3,
        [ObstacleType.DoubleJump]: 2,
      },
      bottleShare: 0.48, // was 0.6 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 0.9,
      hazardWeightScale: 1.7,
      plainWeightScale: 0.7,
      specialWeightScale: 1.5,
    },
    {
      id: 'T2_BLOCKED_LANES',
      title: 'Gesperrte Spuren',
      rowLayout: 'dense',
      fromDistance: 400,
      rowFillChance: 1,
      maxObstaclesPerRow: 3,
      minObstaclesPerRow: 2,
      obstacleWeights: {
        [ObstacleType.Jump]: 2,
        [ObstacleType.Slide]: 2,
        [ObstacleType.LaneChange]: 4,
        [ObstacleType.DoubleJump]: 3,
      },
      bottleShare: 0.41, // was 0.5 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 0.75,
      hazardWeightScale: 2.1,
      plainWeightScale: 0.55,
      specialWeightScale: 1.8,
    },
    {
      id: 'T3_COMPLEX',
      title: 'Profi-Baustelle',
      rowLayout: 'dense',
      fromDistance: 700,
      rowFillChance: 1,
      maxObstaclesPerRow: 3,
      minObstaclesPerRow: 2,
      obstacleWeights: {
        [ObstacleType.Jump]: 2,
        [ObstacleType.Slide]: 2,
        [ObstacleType.LaneChange]: 5,
        [ObstacleType.DoubleJump]: 4,
      },
      bottleShare: 0.36, // was 0.4 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 0.6,
      hazardWeightScale: 2.5,
      plainWeightScale: 0.42,
      specialWeightScale: 2.1,
    },
    {
      id: 'T4_RUSH',
      title: 'Hochbetrieb',
      rowLayout: 'dense',
      fromDistance: 1500,
      rowFillChance: 1,
      maxObstaclesPerRow: 3,
      minObstaclesPerRow: 2,
      obstacleWeights: {
        [ObstacleType.Jump]: 2,
        [ObstacleType.Slide]: 2,
        [ObstacleType.LaneChange]: 6,
        [ObstacleType.DoubleJump]: 5,
      },
      bottleShare: 0.29, // was 0.28 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 0.45,
      hazardWeightScale: 2.9,
      plainWeightScale: 0.33,
      specialWeightScale: 2.4,
    },
    {
      id: 'T5_DEADLINE',
      title: 'Abnahmetermin',
      rowLayout: 'dense',
      fromDistance: 2500,
      rowFillChance: 1,
      maxObstaclesPerRow: 3,
      minObstaclesPerRow: 3,
      obstacleWeights: {
        [ObstacleType.Jump]: 2,
        [ObstacleType.Slide]: 2,
        [ObstacleType.LaneChange]: 6,
        [ObstacleType.DoubleJump]: 6,
      },
      bottleShare: 0.28, // was 0.18 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 0.32,
      hazardWeightScale: 3.2,
      plainWeightScale: 0.28,
      specialWeightScale: 2.6,
    },
    {
      id: 'T6_CHAOS',
      title: 'Baustellen-Chaos',
      rowLayout: 'dense',
      fromDistance: 3500,
      rowFillChance: 1,
      maxObstaclesPerRow: 3,
      minObstaclesPerRow: 3,
      obstacleWeights: {
        [ObstacleType.Jump]: 2,
        [ObstacleType.Slide]: 2,
        [ObstacleType.LaneChange]: 6,
        [ObstacleType.DoubleJump]: 7,
      },
      bottleShare: 0.1, // was 0.13 – user 2026-10-05 late: bottles −10…15 % (measured per band – the denser cranes and drums already take bottle lines)
      bonusShare: 0.24,
      hazardWeightScale: 3.5,
      plainWeightScale: 0.25,
      specialWeightScale: 2.8,
    },
  ],};
